/**
 * The figures shown on the payment page, for one student and for the family.
 *
 * The server already does the arithmetic; this decides which numbers appear on which line and
 * what each one is called, so the student card and the family summary cannot drift apart.
 */

import { currentYear } from './years';

const asNumber = (value) => Number(value) || 0;

export const money = (value) => `$${asNumber(value).toFixed(2)}`;

export const childrenLabel = (count) => `${count} ${count === 1 ? 'child' : 'children'}`;

export const STAFF = 'Staff Discount';
export const EARLY_BIRD = 'Early Bird Discount';
export const HEALTH_CARE_CARD = 'Health Care Card Discount';

/**
 * Every discount that reduced this student's fees, and what to call each one. A student can have
 * more than one — a family discount and a Health Care Card discount, say — so the reasons are
 * listed rather than reduced to a single winner.
 */
export function studentDiscounts(feeRow = {}, {
    familyChildCount = 0,
    familyDiscountOption = '',
    staffDiscount = 'No',
} = {}) {
    const sibling = asNumber(feeRow.sibling_discount);
    const hcc = asNumber(feeRow.HCCDiscount);
    const selected = asNumber(feeRow.discount);
    const resourceOnly = asNumber(feeRow.resource_only_discount);

    const discountType = feeRow.discount_type || familyDiscountOption || '';
    const isStaff = staffDiscount === 'Yes' || discountType === STAFF;
    const isEarlyBird = discountType === EARLY_BIRD;

    const reasons = [];
    // Each discount and its own amount, for the small print under the discount line.
    const parts = [];
    if (sibling > 0) {
        if (familyChildCount > 0) reasons.push(childrenLabel(familyChildCount));
        parts.push({
            label: familyChildCount > 0 ? `Family discount (${childrenLabel(familyChildCount)})` : 'Family discount',
            amount: sibling,
        });
    }
    if (hcc > 0) {
        reasons.push(HEALTH_CARE_CARD);
        parts.push({ label: HEALTH_CARE_CARD, amount: hcc });
    }
    if (selected > 0) {
        const label = isStaff ? STAFF : isEarlyBird ? EARLY_BIRD : (discountType || 'Discount');
        if (isStaff || isEarlyBird || discountType) reasons.push(label);
        parts.push({ label, amount: selected });
    }
    if (resourceOnly > 0) {
        reasons.push('Adelaide 5th student onwards');
        parts.push({ label: 'Adelaide 5th student onwards', amount: resourceOnly });
    }

    return {
        sibling,
        hcc,
        selected,
        resourceOnly,
        total: sibling + hcc + selected + resourceOnly,
        reasons,
        parts,
    };
}

/** "Family discount (3 children) $300.00 · Health Care Card Discount $150.00" */
export const discountPartsNote = (parts = []) =>
    parts.map((part) => `${part.label} ${money(part.amount)}`).join(' · ');

/** "Discount Applied (3 children + Health Care Card Discount)" */
export const discountAppliedLabel = (reasons = [], prefix = 'Discount Applied') =>
    (reasons.length ? `${prefix} (${reasons.join(' + ')})` : prefix);

/**
 * The six lines of a student's card.
 *
 * An Adelaide 5th-student-onwards row waives school fees and deposit both, so its discount can be
 * larger than the school fees on their own — the fees after discount are floored at zero rather
 * than shown as a negative.
 */
export function studentFeeRows(feeRow = {}, context = {}) {
    const schoolFeesWithoutDiscount = asNumber(feeRow.school_fees_wo_any_disc);
    const resourceFees = asNumber(feeRow.resource_fee);
    const depositFee = asNumber(feeRow.deposit_fee);
    const discounts = studentDiscounts(feeRow, context);
    const schoolFeesAfterDiscount = Math.max(0, schoolFeesWithoutDiscount - discounts.total);

    return {
        schoolFeesWithoutDiscount,
        discountTotal: discounts.total,
        discountReasons: discounts.reasons,
        discountLabel: discountAppliedLabel(discounts.reasons),
        discountNote: discountPartsNote(discounts.parts),
        schoolFeesAfterDiscount,
        resourceFees,
        depositFee,
        totalSchoolFees: schoolFeesAfterDiscount + resourceFees,
        minimumReenrolmentFee: depositFee + resourceFees,
    };
}

/**
 * "(Deposit Fee $200.00 per child + Resource Fee)" — how the minimum is made up.
 *
 * A family still owing the current year has that named here too, because it is the sentence that
 * explains why the figure beside it is larger than the fee table would suggest. Leaving it out
 * would make the minimum look like a fee that had gone up.
 */
export const minimumFeeNote = (depositPerChild, outstandingAmount = 0) => {
    const base = `Deposit Fee ${money(depositPerChild)} per child + Resource Fee`;
    const owing = asNumber(outstandingAmount);
    if (owing <= 0) return base;
    return `${base} + ${money(owing)} owing for ${currentYear()}`;
};

/** The small print under "Paying today": whether it is the whole year, and what is left if not. */
export const payingTodayNote = ({ showMinimum = false, balanceDue = 0, allCampusesPaid = false } = {}) => {
    if (allCampusesPaid) return 'Already paid — nothing more to pay';
    const left = asNumber(balanceDue);
    if (showMinimum) {
        return left > 0
            ? `The minimum re-enrolment fee — ${money(left)} left to pay later`
            : 'The minimum re-enrolment fee';
    }
    return 'The full amount for the year';
};
